// src/components/ScannerQR.jsx
import React, { useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";

/**
 * Solución al error "Cannot stop, scanner is not running or paused."
 * - No se llama stop() al decodificar. Solo se notifica al padre.
 * - Limpieza segura al desmontar (stop/clear), ignorando errores si ya no corre.
 * - Evita dobles inicios / dobles paradas con banderas internas.
 * - Lanza la cámara trasera por defecto cuando esté disponible.
 */
export default function ScannerQR({ onDecoded }) {
  const containerId = "qr-reader-container";
  const qrRef = useRef(null);

  // Banderas internas para evitar condiciones de carrera
  const startingRef = useRef(false);
  const runningRef = useRef(false);
  const stoppingRef = useRef(false);
  const handledOnceRef = useRef(false);

  const safeStop = async () => {
    if (!qrRef.current) return;
    if (!runningRef.current || stoppingRef.current) return; // ya parado o en proceso
    stoppingRef.current = true;
    try {
      await qrRef.current.stop().catch(() => {});
      await qrRef.current.clear().catch(() => {});
    } finally {
      runningRef.current = false;
      stoppingRef.current = false;
    }
  };

  useEffect(() => {
    // Asegura que existe el contenedor
    let el = document.getElementById(containerId);
    if (!el) {
      el = document.createElement("div");
      el.id = containerId;
      el.style.width = "100%";
      el.style.maxWidth = "420px";
      el.style.margin = "0 auto";
      document.body.appendChild(el);
    }

    const html5Qr = new Html5Qrcode(containerId);
    qrRef.current = html5Qr;

    const start = async () => {
      if (startingRef.current || runningRef.current) return;
      startingRef.current = true;
      handledOnceRef.current = false;

      try {
        const cams = await Html5Qrcode.getCameras();
        const back =
          cams.find((c) => /back|rear|environment/i.test(c.label || "")) ||
          cams[0];

        if (!back) throw new Error("No se encontró cámara.");

        await html5Qr.start(
          { deviceId: { exact: back.id } },
          { fps: 10, qrbox: 250 },
          // ✅ Solo notificamos. NO paramos aquí el lector.
          (decodedText) => {
            if (handledOnceRef.current) return; // evita múltiples lecturas
            handledOnceRef.current = true;
            onDecoded?.(decodedText);
          },
          // optional error cb por frame (silenciar)
          () => {}
        );

        runningRef.current = true;
      } catch (err) {
        console.error("Error iniciando lector:", err);
      } finally {
        startingRef.current = false;
      }
    };

    start();

    // Limpieza segura al desmontar
    return () => {
      safeStop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDecoded]);

  return (
    <div
      id={containerId}
      style={{ width: "100%", maxWidth: 420, margin: "0 auto" }}
    />
  );
}
